import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Database, 
  FileText, 
  Globe, 
  Settings, 
  Play, 
  Pause, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Zap,
  HardDrive,
  Network,
  BarChart3,
  Eye,
  Edit3,
  Save,
  X,
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { ConversionSpecManager } from '../components/ConversionSpecManager';
import { MemoryRDIControlPanel } from '../components/MemoryRDIControlPanel';

interface SourceConfig {
  id: string;
  type: 'postgresql' | 'oracle' | 'sql_server' | 'db2' | 'csv' | 'api';
  name: string;
  host?: string;
  port?: number;
  database?: string;
  schema?: string;
  path?: string;
  status: 'connected' | 'disconnected' | 'error';
  lastChecked: string;
}

interface SinkConfig {
  id: string;
  type: 'oracle_staging' | 'memory_rdi' | 'tyler_api' | 'csv_export';
  name: string;
  config: Record<string, any>;
  status: 'active' | 'inactive' | 'error';
  lastUsed: string;
}

interface ProcessingConfig {
  batchSize: number;
  parallelWorkers: number;
  timeout: number;
  retryAttempts: number;
  cdcEnabled: boolean;
  cdcLagThreshold: number;
  enableValidation: boolean;
  enableChecksums: boolean;
}

export const PipelineConfigurationTab: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'sources' | 'sinks' | 'processing' | 'monitoring' | 'conversion-specs' | 'memory-rdi'>('sources');
  const [sources, setSources] = useState<SourceConfig[]>([]);
  const [sinks, setSinks] = useState<SinkConfig[]>([]);
  const [processingConfig, setProcessingConfig] = useState<ProcessingConfig>({
    batchSize: 1000,
    parallelWorkers: 4,
    timeout: 300000,
    retryAttempts: 3,
    cdcEnabled: true,
    cdcLagThreshold: 5000,
    enableValidation: true,
    enableChecksums: true
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSource, setEditingSource] = useState<string | null>(null);
  const [editingSink, setEditingSink] = useState<string | null>(null);
  const [showAddSource, setShowAddSource] = useState(false);
  const [showAddSink, setShowAddSink] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Simulate loading pipeline configuration data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setSources([
          {
            id: 'source-1',
            type: 'postgresql',
            name: 'CorePG Production',
            host: 'localhost',
            port: 5432,
            database: 'hivecore',
            schema: 'public',
            status: 'connected',
            lastChecked: '2025-01-01T12:00:00Z'
          },
          {
            id: 'source-2',
            type: 'oracle',
            name: 'Oracle Legacy',
            host: 'oracle-prod.company.com',
            port: 1521,
            database: 'LEGACY',
            schema: 'TYLER_STAGING',
            status: 'connected',
            lastChecked: '2025-01-01T11:58:00Z'
          },
          {
            id: 'source-3',
            type: 'csv',
            name: 'Tyler CSV Import',
            path: '/tmp/tyler_gateway/watch',
            status: 'connected',
            lastChecked: '2025-01-01T11:55:00Z'
          }
        ]);

        setSinks([
          {
            id: 'sink-1',
            type: 'oracle_staging',
            name: 'Oracle Staging Tables',
            config: {
              batchSize: 100,
              outputPath: '/tmp/oracle_staging',
              enableMerge: true
            },
            status: 'active',
            lastUsed: '2025-01-01T12:00:00Z'
          },
          {
            id: 'sink-2',
            type: 'memory_rdi',
            name: 'Memory RDI Live Context',
            config: {
              contextSize: 10000,
              snapshotInterval: 3600,
              enableReplay: true
            },
            status: 'active',
            lastUsed: '2025-01-01T11:59:00Z'
          },
          {
            id: 'sink-3',
            type: 'tyler_api',
            name: 'Tyler API Integration',
            config: {
              baseUrl: 'https://api.tyler.com',
              rateLimit: 100,
              timeout: 30
            },
            status: 'active',
            lastUsed: '2025-01-01T11:58:00Z'
          }
        ]);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load pipeline configuration');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
      case 'active':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'disconnected':
      case 'inactive':
        return <Clock size={16} className="text-yellow-400" />;
      case 'error':
        return <AlertTriangle size={16} className="text-red-400" />;
      default:
        return <Clock size={16} className="text-slate-400" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'postgresql':
      case 'oracle':
      case 'sql_server':
      case 'db2':
        return <Database size={20} className="text-blue-400" />;
      case 'csv':
        return <FileText size={20} className="text-green-400" />;
      case 'api':
        return <Globe size={20} className="text-purple-400" />;
      case 'oracle_staging':
        return <HardDrive size={20} className="text-orange-400" />;
      case 'memory_rdi':
        return <Zap size={20} className="text-yellow-400" />;
      case 'tyler_api':
        return <Network size={20} className="text-cyan-400" />;
      case 'csv_export':
        return <FileText size={20} className="text-emerald-400" />;
      default:
        return <Settings size={20} className="text-slate-400" />;
    }
  };

  const handleSourceAction = async (sourceId: string, action: 'test' | 'start' | 'stop') => {
    console.log(`Performing ${action} on source ${sourceId}`);
    // In real implementation, this would call the appropriate API
  };

  const handleSinkAction = async (sinkId: string, action: 'test' | 'start' | 'stop') => {
    console.log(`Performing ${action} on sink ${sinkId}`);
    // In real implementation, this would call the appropriate API
  };

  const handleProcessingConfigUpdate = (key: keyof ProcessingConfig, value: any) => {
    setProcessingConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-32 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-96 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
          <div className="h-96 bg-slate-800/50 rounded-lg animate-pulse" data-testid="skeleton-placeholder" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle size={48} className="text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Failed to load pipeline configuration</h3>
        <p className="text-slate-400 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pipeline Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Active Sources</h3>
            <Database size={20} className="text-blue-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {sources.filter(s => s.status === 'connected').length}
          </div>
          <p className="text-sm text-slate-400">
            of {sources.length} configured
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Active Sinks</h3>
            <HardDrive size={20} className="text-green-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {sinks.filter(s => s.status === 'active').length}
          </div>
          <p className="text-sm text-slate-400">
            of {sinks.length} configured
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Batch Size</h3>
            <BarChart3 size={20} className="text-purple-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {processingConfig.batchSize.toLocaleString()}
          </div>
          <p className="text-sm text-slate-400">
            records per batch
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="evolut-metric-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400">Parallel Workers</h3>
            <Zap size={20} className="text-yellow-400" />
          </div>
          <div className="text-3xl font-bold text-white mb-2">
            {processingConfig.parallelWorkers}
          </div>
          <p className="text-sm text-slate-400">
            concurrent processes
          </p>
        </motion.div>
      </div>

      {/* Configuration Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Section Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="evolut-metric-card p-6"
        >
          <h3 className="text-lg font-semibold text-white mb-6">Configuration</h3>
          <nav className="space-y-2">
            {[
              { id: 'sources', label: 'Data Sources', icon: Database },
              { id: 'sinks', label: 'Output Sinks', icon: HardDrive },
              { id: 'processing', label: 'Processing', icon: Settings },
              { id: 'monitoring', label: 'Monitoring', icon: Eye },
              { id: 'conversion-specs', label: 'Conversion Specs', icon: FileText },
              { id: 'memory-rdi', label: 'Memory RDI', icon: Zap }
            ].map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id as any)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-all duration-200 ${
                  activeSection === section.id
                    ? 'bg-slate-700/50 text-white'
                    : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                }`}
              >
                <section.icon size={20} />
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Configuration Content */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="lg:col-span-3"
        >
          {activeSection === 'sources' && (
            <div className="evolut-metric-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Data Sources</h3>
                <button
                  onClick={() => setShowAddSource(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Add Source</span>
                </button>
              </div>

              <div className="space-y-4">
                {sources.map((source) => (
                  <motion.div
                    key={source.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {getTypeIcon(source.type)}
                        <div>
                          <h4 className="text-white font-medium">{source.name}</h4>
                          <p className="text-sm text-slate-400">
                            {source.type.toUpperCase()}
                            {source.host && ` • ${source.host}:${source.port}`}
                            {source.path && ` • ${source.path}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(source.status)}
                          <span className="text-sm text-slate-400 capitalize">{source.status}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSourceAction(source.id, 'test')}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Test Connection"
                          >
                            <RefreshCw size={16} className="text-blue-400" />
                          </button>
                          <button
                            onClick={() => setEditingSource(source.id)}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Edit Configuration"
                          >
                            <Edit3 size={16} className="text-yellow-400" />
                          </button>
                          <button
                            onClick={() => handleSourceAction(source.id, 'stop')}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Stop Source"
                          >
                            <Pause size={16} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'sinks' && (
            <div className="evolut-metric-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Output Sinks</h3>
                <button
                  onClick={() => setShowAddSink(true)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white transition-colors flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Add Sink</span>
                </button>
              </div>

              <div className="space-y-4">
                {sinks.map((sink) => (
                  <motion.div
                    key={sink.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {getTypeIcon(sink.type)}
                        <div>
                          <h4 className="text-white font-medium">{sink.name}</h4>
                          <p className="text-sm text-slate-400">
                            {sink.type.replace('_', ' ').toUpperCase()}
                            {sink.config.batchSize && ` • Batch: ${sink.config.batchSize}`}
                            {sink.config.contextSize && ` • Context: ${sink.config.contextSize}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(sink.status)}
                          <span className="text-sm text-slate-400 capitalize">{sink.status}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSinkAction(sink.id, 'test')}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Test Sink"
                          >
                            <RefreshCw size={16} className="text-blue-400" />
                          </button>
                          <button
                            onClick={() => setEditingSink(sink.id)}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Edit Configuration"
                          >
                            <Edit3 size={16} className="text-yellow-400" />
                          </button>
                          <button
                            onClick={() => handleSinkAction(sink.id, 'stop')}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                            title="Stop Sink"
                          >
                            <Pause size={16} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'processing' && (
            <div className="evolut-metric-card p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Processing Configuration</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      Batch Size
                    </label>
                    <input
                      type="number"
                      value={processingConfig.batchSize}
                      onChange={(e) => handleProcessingConfigUpdate('batchSize', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      Parallel Workers
                    </label>
                    <input
                      type="number"
                      value={processingConfig.parallelWorkers}
                      onChange={(e) => handleProcessingConfigUpdate('parallelWorkers', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      Timeout (ms)
                    </label>
                    <input
                      type="number"
                      value={processingConfig.timeout}
                      onChange={(e) => handleProcessingConfigUpdate('timeout', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      Retry Attempts
                    </label>
                    <input
                      type="number"
                      value={processingConfig.retryAttempts}
                      onChange={(e) => handleProcessingConfigUpdate('retryAttempts', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400">
                      Enable CDC
                    </label>
                    <button
                      onClick={() => handleProcessingConfigUpdate('cdcEnabled', !processingConfig.cdcEnabled)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        processingConfig.cdcEnabled ? 'bg-blue-600' : 'bg-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          processingConfig.cdcEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-400 mb-2">
                      CDC Lag Threshold (ms)
                    </label>
                    <input
                      type="number"
                      value={processingConfig.cdcLagThreshold}
                      onChange={(e) => handleProcessingConfigUpdate('cdcLagThreshold', parseInt(e.target.value))}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400">
                      Enable Validation
                    </label>
                    <button
                      onClick={() => handleProcessingConfigUpdate('enableValidation', !processingConfig.enableValidation)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        processingConfig.enableValidation ? 'bg-blue-600' : 'bg-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          processingConfig.enableValidation ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-400">
                      Enable Checksums
                    </label>
                    <button
                      onClick={() => handleProcessingConfigUpdate('enableChecksums', !processingConfig.enableChecksums)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        processingConfig.enableChecksums ? 'bg-blue-600' : 'bg-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          processingConfig.enableChecksums ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-4">
                <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors">
                  Reset
                </button>
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors flex items-center space-x-2">
                  <Save size={16} />
                  <span>Save Configuration</span>
                </button>
              </div>
            </div>
          )}

          {activeSection === 'monitoring' && (
            <div className="evolut-metric-card p-6">
              <h3 className="text-lg font-semibold text-white mb-6">Pipeline Monitoring</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="text-white font-medium">Source Health</h4>
                  {sources.map((source) => (
                    <div key={source.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getTypeIcon(source.type)}
                        <span className="text-white">{source.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(source.status)}
                        <span className="text-sm text-slate-400">
                          {new Date(source.lastChecked).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  <h4 className="text-white font-medium">Sink Performance</h4>
                  {sinks.map((sink) => (
                    <div key={sink.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getTypeIcon(sink.type)}
                        <span className="text-white">{sink.name}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(sink.status)}
                        <span className="text-sm text-slate-400">
                          {new Date(sink.lastUsed).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-white font-medium mb-4">Processing Metrics</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-white">1,247</div>
                    <div className="text-sm text-slate-400">Records Processed</div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-white">2.3s</div>
                    <div className="text-sm text-slate-400">Avg Processing Time</div>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="text-2xl font-bold text-white">99.8%</div>
                    <div className="text-sm text-slate-400">Success Rate</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'conversion-specs' && (
            <ConversionSpecManager />
          )}

          {activeSection === 'memory-rdi' && (
            <MemoryRDIControlPanel />
          )}
        </motion.div>
      </div>
    </div>
  );
};